import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import { AUTH } from '@/constants/testIds';
import { FEATURES } from '@/config/features';
import { PasswordInput } from '@/components/PasswordInput';
import { SquareCardForm } from '@/components/SquareCardForm';
import { SecuritySection } from '@/components/SecuritySection';
import {
  Sparkles, ShieldCheck, CreditCard, RefreshCw, XCircle, Check,
  AlertTriangle, KeyRound, FolderPlus, X as XIcon,
} from 'lucide-react';

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

// -------------------------- Profile ---------------------------------------

const ProfileSection = ({ user, setUser }) => {
  const [name, setName] = useState(user?.display_name || '');
  const [zip, setZip] = useState(user?.zip_code || '');
  const [radius, setRadius] = useState(user?.default_radius_miles ?? 35);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch('/settings/profile', {
        display_name: name || null,
        zip_code: zip || null,
        default_radius_miles: Number(radius) || 35,
      });
      setUser(data);
      toast.success('Profile updated');
    } catch {
      toast.error('Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="zita-card p-6 space-y-4" data-testid="settings-profile">
      <h2 className="font-serif text-2xl text-zita-espresso">Profile</h2>
      <div className="text-xs text-zita-mocha">Signed in as <span className="text-zita-espresso">{user?.email}</span></div>
      <div>
        <label className="block text-xs uppercase tracking-wider text-zita-muted mb-1">Display name (optional)</label>
        <input value={name} onChange={(e) => setName(e.target.value)} className="zita-input w-full px-4 py-3 text-sm" data-testid="settings-profile-name" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs uppercase tracking-wider text-zita-muted mb-1">ZIP code</label>
          <input value={zip} onChange={(e) => setZip(e.target.value)} className="zita-input w-full px-4 py-3 text-sm" data-testid="settings-profile-zip" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-zita-muted mb-1">Radius (mi)</label>
          <input type="number" value={radius} onChange={(e) => setRadius(e.target.value)} className="zita-input w-full px-4 py-3 text-sm" data-testid="settings-profile-radius" />
        </div>
      </div>
      <button type="button" onClick={save} disabled={busy} className="zita-btn-primary" data-testid="settings-profile-save">
        {busy ? 'Saving…' : 'Save profile'}
      </button>
    </section>
  );
};

// -------------------------- Change Password -------------------------------

const ChangePasswordSection = () => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error('New password must be at least 8 characters.'); return; }
    if (next !== confirm) { toast.error('Passwords do not match.'); return; }
    if (next === current) { toast.error('New password must be different from the current one.'); return; }
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      toast.success('Password updated. Other sessions signed out.');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change password.');
    } finally { setBusy(false); }
  };

  return (
    <section className="zita-card p-6 space-y-4" data-testid="settings-change-password">
      <div className="flex items-center gap-2">
        <KeyRound size={16} className="text-zita-cocoa" />
        <h2 className="font-serif text-2xl text-zita-espresso">Change password</h2>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-xs uppercase tracking-wider text-zita-muted mb-1">Current password</label>
          <PasswordInput value={current} onChange={(e) => setCurrent(e.target.value)} testId={AUTH.changePwCurrent} required autoComplete="current-password" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-zita-muted mb-1">New password</label>
          <PasswordInput value={next} onChange={(e) => setNext(e.target.value)} testId={AUTH.changePwNew} required minLength={8} autoComplete="new-password" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-zita-muted mb-1">Confirm new password</label>
          <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} testId={AUTH.changePwConfirm} required minLength={8} autoComplete="new-password" />
        </div>
        <button type="submit" disabled={busy || !current || !next || !confirm} className="zita-btn-primary" data-testid={AUTH.changePwSubmit}>
          {busy ? 'Updating…' : 'Update password'}
        </button>
      </form>
      <p className="text-[11px] text-zita-muted">
        For your safety, changing your password signs out all other sessions.
      </p>
    </section>
  );
};

// -------------------------- Subscription ----------------------------------

const StatusPill = ({ user }) => {
  const st = user.subscription_status;
  const map = {
    trialing: { c: 'bg-amber-100 text-amber-900', l: 'Free trial' },
    active: { c: 'bg-emerald-100 text-emerald-800', l: 'Active' },
    past_due: { c: 'bg-red-100 text-red-800', l: 'Past due' },
    canceled: { c: 'bg-zita-taupe/40 text-zita-mocha', l: 'Canceled' },
    unpaid: { c: 'bg-red-100 text-red-800', l: 'Unpaid' },
    incomplete: { c: 'bg-amber-100 text-amber-900', l: 'Incomplete' },
    none: { c: 'bg-zita-taupe/40 text-zita-mocha', l: 'No subscription' },
  };
  const it = map[st] || map.none;
  if (user.is_admin) return <span className="text-xs px-2 py-1 rounded-full bg-ari/10 ari-accent">Owner — permanent access</span>;
  return <span className={`text-xs px-2 py-1 rounded-full ${it.c}`}>{it.l}</span>;
};

const SubscriptionSection = ({ user, refresh }) => {
  const [busy, setBusy] = useState(null);
  const [showCardModal, setShowCardModal] = useState(null); // 'subscribe' | 'update' | 'addon' | null
  const [square, setSquare] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/billing/plans').then((r) => setSquare(r.data?.square || null)).catch(() => {});
  }, []);

  const handleCardToken = async (token, cardholderName) => {
    const mode = showCardModal;
    setBusy(mode);
    try {
      if (mode === 'update') {
        await api.post('/billing/update-card', { token, cardholder_name: cardholderName || null });
        await refresh();
        toast.success('Card updated on your active subscriptions.');
      } else {
        // Initial Zita subscription — Aug 2026 doctrine consolidated the
        // Ari Search+ add-on into Zita Pro. include_addon must be false.
        await api.post('/billing/attach-card', { token, cardholder_name: cardholderName || null });
        await api.post('/billing/activate', { include_main: true, include_addon: false });
        await refresh();
        toast.success('Subscription active. Welcome to Zita Pro.');
      }
      setShowCardModal(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not process your card.');
    } finally {
      setBusy(null);
    }
  };

  const cancel = async (which) => {
    if (!window.confirm(
      which === 'addon'
        ? 'Cancel Ari Search+? You keep access until the end of your current billing period. No prorated refund.'
        : 'Cancel your Zita subscription? You keep access until the end of your current billing period. No prorated refund.'
    )) return;
    setBusy(`cancel-${which}`);
    try {
      await api.post(which === 'addon' ? '/billing/cancel-addon' : '/billing/cancel');
      await refresh();
      toast.success('Cancellation scheduled at the end of the current period.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel.');
    } finally { setBusy(null); }
  };

  const resume = async (which) => {
    setBusy(`resume-${which}`);
    try {
      await api.post(which === 'addon' ? '/billing/resume-addon' : '/billing/resume');
      await refresh();
      toast.success('Automatic billing re-enabled.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not resume.');
    } finally { setBusy(null); }
  };

  const openSubscribeFlow = () => {
    // Trialing users → dedicated /subscribe page (bigger canvas, marketing feel)
    // Active users (upgrading) → inline modal
    if (user.subscription_status === 'trialing' || !user.has_billing_account) {
      navigate('/subscribe');
    } else {
      setShowCardModal('subscribe');
    }
  };

  const hasSub = !!user.has_billing_account && user.subscription_status !== 'none';
  const hasAddon = user.collections_addon_active && !user.is_admin;
  const isTrialing = user.subscription_status === 'trialing';
  const isActive = user.subscription_status === 'active';
  const isPastDue = user.subscription_status === 'past_due';
  const isCanceling = !!user.cancel_at_period_end && hasSub;

  return (
    <section className="zita-card p-6 space-y-5" data-testid="settings-subscription">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CreditCard size={16} className="text-zita-cocoa" />
          <h2 className="font-serif text-2xl text-zita-espresso">Subscription</h2>
        </div>
        <StatusPill user={user} />
      </div>

      {isPastDue && (
        <div className="flex items-start gap-2 border border-red-200 bg-red-50 text-red-800 rounded-lg p-3 text-sm">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            Your last payment failed. Update your card to keep access.
            <button
              type="button"
              onClick={() => setShowCardModal('update')}
              className="ml-2 underline"
              data-testid="settings-sub-update-card-inline"
            >
              Update card
            </button>
          </div>
        </div>
      )}

      {user.is_admin ? (
        <div className="flex items-center gap-3 rounded-lg bg-ari/10 p-3">
          <Sparkles size={16} className="ari-accent" />
          <div className="text-sm text-zita-espresso">
            <div className="font-semibold">Owner access active</div>
            <div className="text-zita-mocha text-xs">You have permanent unlimited access. No Square charges apply.</div>
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-zita-sand/40 p-3">
            <div className="text-[10px] uppercase tracking-wider text-zita-mocha">Current plan</div>
            <div className="text-zita-espresso font-medium">
              {isTrialing ? 'Zita — 3-day free trial' : isActive || isCanceling ? 'Zita Monthly ($29.99 / month)' : hasSub ? 'Zita Monthly' : 'Not subscribed'}
            </div>
          </div>
          <div className="rounded-lg bg-zita-sand/40 p-3" data-testid="settings-sub-renewal">
            <div className="text-[10px] uppercase tracking-wider text-zita-mocha">
              {isTrialing ? 'Trial ends' : isCanceling ? 'Access ends' : 'Next renewal'}
            </div>
            <div className="text-zita-espresso font-medium">
              {isTrialing ? formatDate(user.trial_end) : formatDate(user.current_period_end)}
            </div>
            {isCanceling && (
              <div className="text-[10px] text-amber-800 mt-1">
                Cancellation scheduled — no further charges after this date.
              </div>
            )}
          </div>
        </div>
      )}

      {!user.is_admin && (
        <div className="flex flex-wrap gap-2">
          {(!hasSub || !isActive) && !isCanceling && (
            <button
              type="button"
              onClick={openSubscribeFlow}
              disabled={busy === 'subscribe'}
              data-testid="settings-sub-subscribe"
              className="zita-btn-primary text-sm inline-flex items-center gap-2"
            >
              <CreditCard size={14} />
              {isTrialing ? 'Subscribe now ($29.99 / month)' : 'Subscribe ($29.99 / month)'}
            </button>
          )}

          {hasSub && (
            <button
              type="button"
              onClick={() => setShowCardModal('update')}
              disabled={busy === 'update'}
              data-testid="settings-sub-update-card"
              className="zita-btn-secondary text-sm inline-flex items-center gap-2"
            >
              <RefreshCw size={14} />
              {user.card_last_4 ? `Update card · ${user.card_brand || 'Card'} •••• ${user.card_last_4}` : 'Update payment method'}
            </button>
          )}

          {isActive && !isCanceling && (
            <button
              type="button"
              onClick={() => cancel('membership')}
              disabled={busy === 'cancel-membership'}
              data-testid="settings-sub-cancel"
              className="zita-btn-secondary text-sm inline-flex items-center gap-2 text-red-700 border-red-300 hover:bg-red-50"
            >
              <XCircle size={14} /> {busy === 'cancel-membership' ? 'Cancelling…' : 'Cancel subscription'}
            </button>
          )}

          {isCanceling && (
            <button
              type="button"
              onClick={() => resume('membership')}
              disabled={busy === 'resume-membership'}
              data-testid="settings-sub-resume"
              className="zita-btn-primary text-sm inline-flex items-center gap-2"
            >
              <Check size={14} /> {busy === 'resume-membership' ? 'Re-enabling…' : 'Turn automatic billing back on'}
            </button>
          )}
        </div>
      )}

      {/* Ari Search+ retired (Aug 2026) — every capability is now included in
          Zita Pro. Legacy addon subscribers keep their access transparently. */}
      {(user.collections_addon_active && !user.is_admin) && (
        <div className="border-t border-zita-taupe/40 pt-5" data-testid="settings-legacy-addon">
          <div className="rounded-lg bg-zita-sand/40 p-3 text-sm text-zita-espresso flex items-start gap-2">
            <Sparkles size={16} className="text-zita-cocoa mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">Ari Search+ is now included in Zita Pro</div>
              <div className="text-xs text-zita-mocha mt-1">
                Your legacy Ari Search+ add-on has been consolidated into your Zita Pro
                membership at no additional charge. All previous Search+ features
                (deep search, unlimited folders, exports, Ari-suggested categorization)
                are now part of the flat $29.99 / month plan.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="border-t border-zita-taupe/40 pt-5">
        <div className="text-xs text-zita-muted leading-relaxed">
          <strong>Cancellation policy.</strong> You keep access through the end of the current
          billing period. No partial or prorated refunds. Your account locks at the end of the
          period until you subscribe again.
        </div>
      </div>

      {/* Card entry modal — used for "update card", "subscribe" (active users
          coming from Settings), and "addon" (adding Ari Search+ when there is
          no card on file yet). */}
      {showCardModal && square?.configured && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          role="dialog"
          data-testid="settings-card-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-serif text-2xl text-zita-espresso">
                  {showCardModal === 'update' ? 'Update payment method' : 'Subscribe to Zita Pro'}
                </h3>
                <p className="text-xs text-zita-mocha mt-1">
                  {showCardModal === 'update'
                    ? 'Your new card replaces the one on file for your active Zita Pro subscription.'
                    : 'Your card will be charged $29.99 / month for Zita Pro. Every capability is included; there are no separate add-ons.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowCardModal(null)}
                aria-label="Close"
                data-testid="settings-card-modal-close"
                className="text-zita-mocha hover:text-zita-espresso"
              >
                <XIcon size={18} />
              </button>
            </div>
            <SquareCardForm
              applicationId={square.application_id}
              locationId={square.location_id}
              submitLabel={showCardModal === 'update' ? 'Update card' : 'Save card & activate'}
              cardholderName={user.display_name || ''}
              busy={!!busy}
              onToken={handleCardToken}
            />
          </div>
        </div>
      )}
    </section>
  );
};

// -------------------------- Page shell ------------------------------------

const Settings = () => {
  const { user, setUser, refresh } = useAuth();

  useEffect(() => {
    // Ensure user object is fresh whenever user visits settings
    refresh?.();
  }, [refresh]);

  if (!user) return null;

  return (
    <div className="space-y-6 max-w-2xl">
      <header>
        <h1 className="font-serif text-4xl text-zita-espresso">Settings</h1>
        <p className="mt-1 text-zita-mocha">Profile, subscription, and password.</p>
      </header>

      <SubscriptionSection user={user} refresh={refresh} />

      <SecuritySection />

      <ChangePasswordSection />

      <ProfileSection user={user} setUser={setUser} />

      {FEATURES.remoddashPromo && (
        <section className="zita-card p-6 space-y-3" data-testid="settings-business-tools">
          <h2 className="font-serif text-2xl text-zita-espresso">Business Tools</h2>
          <p className="text-sm text-zita-mocha leading-relaxed">
            Zita finds opportunities. If you also need to <em>manage</em> customers,
            scheduling, estimates, invoices, payments, or dispatch around them,
            <span className="font-semibold"> RemodDash</span> — our sister platform in
            the <span className="remod-accent font-semibold">RemodWorks</span> AI
            Ecosystem — is built for that.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <a href="https://remoddash.com" target="_blank" rel="noopener noreferrer" data-testid="settings-remoddash-open" className="zita-btn-secondary text-sm inline-flex items-center gap-2">
              Open RemodDash ↗
            </a>
            <a href="https://remoddash.com/learn" target="_blank" rel="noopener noreferrer" data-testid="settings-remoddash-learn" className="text-sm text-zita-mocha hover:text-zita-cocoa underline">
              Learn more
            </a>
          </div>
        </section>
      )}
    </div>
  );
};

export default Settings;
